import type { TPermission } from '@semios/app-platform-value-type-definitions'
import { getValueTypeHemisphere } from 'App/data-export/store/selectors/_utils/getValueTypeHemisphere'
import { valueTypesTimeseries } from 'settings/valueTypesTimeseries'
import type { TFieldAssetStoreState } from 'stores/fieldAssetStore/fieldAssetStore'
import { checkAuthorization } from 'utils/checkAuthorization'

type TDataExportValueType = {
  permissionId: string
  section: string
  valueGroup: string
  valueType: string
  interval: string
  fieldAssetType: string
}

const dataExportValueTypes: TDataExportValueType[] = valueTypesTimeseries
  .filter((x) => x.is_available_for_data_export)
  .map((x) => ({
    permissionId: x.permission_id,
    section: x.section_id,
    valueGroup: x.value_type_group_id,
    valueType: x.id,
    interval: x.aggregation,
    fieldAssetType: x.field_asset,
  }))

const checkHemisphere = ({
  valueType,
  isSouthernHemisphere,
}: {
  valueType: string
  isSouthernHemisphere: boolean
}): boolean => {
  const valueTypeHemisphere = getValueTypeHemisphere(valueType)

  if (
    valueTypeHemisphere === 'BOTH_HEMISPHERES' ||
    (valueTypeHemisphere === 'SOUTHERN_HEMISPHERE' && isSouthernHemisphere) ||
    (valueTypeHemisphere === 'NORTHERN_HEMISPHERE' && !isSouthernHemisphere)
  ) {
    return true
  } else {
    return false
  }
}

export const getDataExportValueTypes = ({
  fieldAssetStoreState,
  propertyId,
}: {
  fieldAssetStoreState: TFieldAssetStoreState
  propertyId: number
}) => {
  const property = fieldAssetStoreState.properties?.[propertyId]

  if (!property) {
    return []
  }

  const results: Array<
    TDataExportValueType & {
      fieldAssetId: string
      fieldAssetName: string
      propertyId: number
      propertyName: string
    }
  > = []

  Object.entries(property.blocks ?? {}).forEach(([blockId, block]) => {
    block.valuesTimeseries.forEach((valueType) => {
      dataExportValueTypes.forEach((vt) => {
        if (
          vt.valueType === valueType &&
          vt.fieldAssetType === 'blocks' &&
          checkAuthorization({ permission: vt.permissionId as TPermission, entity: property.propertyId }) &&
          checkHemisphere({ valueType: vt.valueType, isSouthernHemisphere: property.isSouthernHemisphere })
        ) {
          results.push({
            ...vt,
            fieldAssetId: blockId,
            fieldAssetName: block.name || '',
            propertyId: property.propertyId,
            propertyName: property.propertyName,
          })
        }
      })
    })
  })

  Object.entries(property.points ?? {}).forEach(([lngLat, point]) => {
    point.valuesTimeseries.forEach((valueType) => {
      dataExportValueTypes.forEach((vt) => {
        if (
          vt.valueType === valueType &&
          vt.fieldAssetType === 'points' &&
          checkAuthorization({ permission: vt.permissionId as TPermission, entity: property.propertyId }) &&
          checkHemisphere({ valueType: vt.valueType, isSouthernHemisphere: property.isSouthernHemisphere })
        ) {
          results.push({
            ...vt,
            fieldAssetId: lngLat,
            fieldAssetName: point.name || '',
            propertyId: property.propertyId,
            propertyName: property.propertyName,
          })
        }
      })
    })
  })

  Object.entries(property.irrigationZoneEmitters ?? {}).forEach(
    ([irrigationZoneEmitterId, irrigationZoneEmitter]) => {
      irrigationZoneEmitter.valuesTimeseries.forEach((valueType) => {
        dataExportValueTypes.forEach((vt) => {
          if (
            vt.valueType === valueType &&
            vt.fieldAssetType === 'irrigation_zones' &&
            checkAuthorization({ permission: vt.permissionId as TPermission, entity: property.propertyId }) &&
            checkHemisphere({ valueType: vt.valueType, isSouthernHemisphere: property.isSouthernHemisphere })
          ) {
            results.push({
              ...vt,
              fieldAssetId: irrigationZoneEmitterId,
              fieldAssetName: irrigationZoneEmitter.name || '',
              propertyId: property.propertyId,
              propertyName: property.propertyName,
            })
          }
        })
      })
    },
  )

  return results
}
